import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import { Button } from '@/components/Button';
import { chaves } from '@/features/tickets/tickets.api';
import type { Chamado } from '@/features/tickets/types';
import { AvisoDeFalha, CLASSE_DE_CAMPO } from '@/features/workflow/componentes';
import { PainelDeTransicoes } from '@/features/workflow/PainelDeTransicoes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { versoesPublicadas } from '@/features/workflow/regras';
import { chavesDeFluxo, listarDefinicoes, vincularFluxo } from '@/features/workflow/workflow.api';

/**
 * Quem manda na situação deste chamado: o fluxo, ou o seletor antigo.
 *
 * A troca acontece aqui, num lugar só, para que o detalhe do chamado não
 * precise conhecer o motor: ele entrega o controle antigo como filho e este
 * componente decide se ele ainda vale.
 *
 * O critério é `workflowVersionId`. Enquanto ele for nulo, a situação muda por
 * `POST /tickets/:id/status`, que é o que o controle antigo faz. A partir do
 * vínculo, o servidor recusa aquele caminho com 409 — e o que vale são as
 * transições.
 */
export function ControleDeFluxo({
  chamado,
  children,
}: {
  chamado: Chamado;
  children: ReactNode;
}) {
  if (chamado.workflowVersionId !== null) {
    return <PainelDeTransicoes chamado={chamado} />;
  }

  return (
    <div className="space-y-3">
      {children}
      <VincularAoFluxo chamado={chamado} />
    </div>
  );
}

/**
 * Colocar um chamado dentro de um fluxo.
 *
 * Operação explícita, e não efeito colateral da abertura: a maioria dos
 * chamados existe sem que fluxo nenhum se aplique a eles, e é assim que deve
 * ser. Quem vincula é quem administra o processo — por isso o controle só
 * aparece para quem consegue **ler** o catálogo de fluxos (`workflow.read`).
 * Para os demais a consulta responde 403 e a seção simplesmente não existe:
 * não é uma ação bloqueada que valha explicar, é uma ação que não é deles.
 */
function VincularAoFluxo({ chamado }: { chamado: Chamado }) {
  const cliente = useQueryClient();
  const [escolhida, setEscolhida] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  const catalogo = useQuery({
    queryKey: chavesDeFluxo.definicoes,
    queryFn: listarDefinicoes,
    // Sem retentativa: 403 aqui é resposta definitiva, e insistir só atrasaria
    // a renderização da tela para quem não administra fluxo nenhum.
    retry: false,
    staleTime: 5 * 60_000,
  });

  const vincular = useMutation({
    mutationFn: (versionId: string) => vincularFluxo(chamado.id, versionId),
    onMutate: () => setFalha(null),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.raiz }),
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  if (catalogo.isPending || catalogo.isError) return null;

  const publicadas = versoesPublicadas(catalogo.data);
  if (publicadas.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <label htmlFor="fluxo-do-chamado" className="flex items-center gap-1.5 text-sm font-medium">
        <GitBranch className="h-4 w-4 text-muted-foreground" aria-hidden />
        Colocar num fluxo
      </label>
      <p className="text-xs text-muted-foreground">
        A partir do vínculo, a situação passa a ser consequência das transições — o seletor acima
        deixa de valer para este chamado. Trocar de fluxo depois é migração, e migração é operação
        própria.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <select
          id="fluxo-do-chamado"
          value={escolhida}
          onChange={(evento) => setEscolhida(evento.target.value)}
          className={`${CLASSE_DE_CAMPO} max-w-xs`}
        >
          <option value="">Escolha um fluxo publicado…</option>
          {publicadas.map((opcao) => (
            <option key={opcao.versionId} value={opcao.versionId}>
              {opcao.nome} · v{opcao.numero}
            </option>
          ))}
        </select>

        <Button
          size="sm"
          disabled={escolhida === ''}
          loading={vincular.isPending}
          onClick={() => vincular.mutate(escolhida)}
        >
          Vincular
        </Button>
      </div>

      {falha ? <AvisoDeFalha falha={falha} /> : null}
    </div>
  );
}
